import { ItemTypes, MASKS_TYPE, uuid } from '../constants/constants';
import { gridUnit } from '../constants/gridConfig';
import {
  isATitleBox,
  isAVariableBox,
  mutateAddBoxes,
  mutateDefaultBoxValue,
  reinitializeBoxWidthAndHeight,
} from './boxes';
import {
  computeColumnWidth,
  containerColumnGap,
  getColumnAvailableWidth,
  mutateAddColumns,
} from './columns';
import {
  maxContainerHeight,
  mutateRemmoveSectionContainers,
  mutateRemoveContainer,
} from './containers';
import {
  findPageBackground,
  hasOwnPageBackground,
  sectionPageBackgroundId,
} from './pageBackground';
import {
  mutateMergeStyles,
  mutateMergeTmpStyles,
  resolveSectionStyleId,
} from './styles';
import { indentTitles } from './titles';
import {
  mutateExecuteParentTriggers,
  mutateInjectTriggersSection,
  mutateSearchAndLaunchTrigger,
  mutateUpdateTriggerIds,
} from './triggers';
import {
  mutateSortVariableFromSection,
  mutateTakeOutItem,
} from './variableOrderArray';
import { mutateInjectVariablesInBox } from './variables';

// Noms des sections/modèles déjà chargés, alimenté par getTemplate (store/api.js).
// draft.sections n'est peuplé que par getTemplates, lancé en parallèle de
// fetchDatas (cf. Providers) : rien ne garantit qu'il soit là quand le document
// injecte ses sections, qui n'y trouvaient alors aucun nom. Et les
// modèles récursifs n'y figurent jamais — la liste ne contient que les sections.
// Clés en chaîne : l'identifiant vient tantôt de la base (nombre), tantôt du
// contenu d'une box (childTemplateId), et une clé du mauvais type manquerait
// silencieusement sa cible.
const _sectionNames = new Map();
export const rememberSectionName = (id, name) => {
  if (id != null && name) _sectionNames.set(String(id), name);
};
const resolveSectionTitle = (draft, sectionId) =>
  draft.sections.find((s) => s.id === sectionId)?.name ??
  _sectionNames.get(String(sectionId));

// Copie le décor de page propre d'une section dans le document qui l'accueille
// et rend l'id du décor qui revient à ses pages. Sans décor propre, la section
// garde celui dont elle hérite : le décor du document, ou celui de la section
// parente quand elle est imbriquée.
//
// L'id du masque baké est déterministe (sectionPageBackgroundId) : réinjecter la
// même section — resynchro d'une section liée, réouverture du document — réécrit
// son décor au lieu d'en empiler un second.
export const mutateBakeSectionPageBackground = ({
  draft,
  sectionContent,
  sectionId,
  inheritedPageBackgroundId,
}) => {
  if (!hasOwnPageBackground(sectionContent.configuration))
    return inheritedPageBackgroundId;

  const maskId = sectionPageBackgroundId(sectionId);
  const sectionBackground = findPageBackground(sectionContent.masks);
  // Les box du décor vivent désormais dans le document d'accueil : sans
  // nouveaux ids, deux sections issues d'un même modèle partageraient les leurs.
  const boxes = (sectionBackground?.boxes ?? []).map((b) => ({
    ...b,
    oldId: b.id,
    id: uuid(),
    maskId,
  }));
  boxes.forEach((b) => {
    if (b.type === ItemTypes.GROUP_BOXES) {
      b.boxIds = boxes
        .filter((candidate) => b.boxIds?.includes(candidate.oldId))
        .map(({ id }) => id);
    }
  });
  boxes.forEach((b) => {
    delete b.oldId;
  });

  const bakedMask = {
    ...MASKS_TYPE.BACKGROUND,
    ...sectionBackground,
    id: maskId,
    // D'où vient ce décor : sert à le retrouver pour le remplacer ou le purger.
    sectionId,
    boxes,
  };
  const existingIdx = draft.masks.findIndex((m) => m.id === maskId);
  if (existingIdx === -1) draft.masks.push(bakedMask);
  else draft.masks[existingIdx] = bakedMask;

  return maskId;
};

export const mutateFormatSectionBox = ({ box, type, draft }) => {
  if (type === ItemTypes.CHECKBOXS_VARIABLE || type === ItemTypes.QUESTION) {
    box = {
      ...box,
      checkboxStyleId: 0,
    };
  }
  if (type === ItemTypes.SELECTOR_VARIABLE) {
    box = {
      ...box,
      selectorStyleId: 0,
    };
  }
  if (type === ItemTypes.TEXT_VARIABLE) {
    box = {
      ...box,
      textStyleId: 0,
    };
  }
  box = mutateInjectVariablesInBox({
    variables: draft.variables,
    box,
  });
};

export const mutateInjectSection = ({
  draft,
  sectionContent,
  boxId,
  sectionId,
  linked_section = false,
  areItemsChangedByTriggers = false,
  fromSectionId = sectionId,
  dateTimestamp = Date.now(),
  fromBoxId = boxId,
  from_linked_section = linked_section &&
    draft.currentDocument.type === 'template',
  sectionsIds = [sectionId],
  wasDocumentLoading,
  skipContainerRemoval = false,
  opportunityIdOverride = null,
  documentTypeOverride = null,
  parentModel = null,
  parentId = null,
  visitedPath = new Set(),
  // Set des sectionId encore valides (catalogue is_shown) pour le document en
  // cours. Fourni par le point d'entrée (applyTemplate/applyProposal/
  // expandLinkedSectionsAtCreation) et propagé à travers la récursion des
  // sections imbriquées, cf. le check plus bas sur les SECTION_LINK trouvés
  // dans le contenu injecté : sans lui, un lien mort imbriqué (section
  // retirée du catalogue) est injecté quand même car seul le 1er niveau
  // (contenu propre du document ouvert) est vérifié par les appelants.
  validSectionIds,
}) => {
  // Garde : sans contenu de section (fetch échoué), ne rien injecter plutôt que
  // de déréférencer sectionContent.* et créer une box vide.
  if (!sectionContent) return;
  if (fromBoxId === boxId) {
    draft.oldTriggers = { ...draft.triggers };
    draft.oldVariableOrderArray = [...draft.variableOrderArray];
  }
  const sectionTitle = resolveSectionTitle(draft, sectionId);
  // Seul le modèle garde à part (draft.tmp) les styles d'une section liée, et
  // seulement si son contenu est lui aussi retiré à l'enregistrement puis
  // réinjecté au chargement (from_linked_section). Une sous-section liée d'une
  // section en Édition est enregistrée avec le reste : ses styles doivent
  // rejoindre la configuration, sinon ses box désignent des ids perdus.
  const linkedInTemplate =
    draft.currentDocument.type === 'template' &&
    linked_section &&
    from_linked_section;
  if (linkedInTemplate) {
    mutateMergeTmpStyles({
      draft,
      sectionConfiguration: sectionContent.configuration,
      sectionId: sectionId,
      sectionTitle,
      dateTimestamp,
    });
  }
  // Hors modèle (opportunité, offre), les styles d'une section liée rejoignent
  // la configuration du document : ses box désignent des ids suffixés qui
  // doivent y exister.
  const stylesMerged = !linkedInTemplate;
  if (stylesMerged) {
    mutateMergeStyles({
      draft,
      sectionConfiguration: sectionContent.configuration,
      sectionId: sectionId,
      sectionTitle,
      dateTimestamp,
    });
  }
  const box = draft.boxes.find((b) => b.id === boxId);
  const containerIdx = draft.containers.findIndex((c) =>
    c.columnsIds.includes(box.columnId)
  );
  const parentContainerId = draft.containers[containerIdx].id;
  // Zone de section liée du document (cf. utils/linkedSectionZones) : ce qui
  // s'injecte depuis une box de la zone en fait partie et sera rechargé avec.
  const linkedSectionZone =
    draft.currentDocument.type !== 'template'
      ? draft.containers[containerIdx].linkedSection
      : undefined;
  // Décor de page des pages de cette section, résolu ici une fois : le container
  // qui porte l'ancre est celui de la section parente (ou du document), et
  // porte donc déjà le décor dont hériter. Chaque container injecté l'emporte,
  // et le service PDF n'a plus qu'à le lire (cf. utils/pageBackground).
  const pageBackgroundId = mutateBakeSectionPageBackground({
    draft,
    sectionContent,
    sectionId,
    inheritedPageBackgroundId:
      draft.containers[containerIdx].pageBackgroundId,
  });
  const columnsIdDict = {};
  sectionContent.columns.forEach((c) => {
    columnsIdDict[c.id] = uuid();
  });
  // Lignes imbriquées de la section (cf. rowContainers dans store/containers.js).
  // Leurs sous-colonnes sont dans sectionContent.columns comme les autres, mais
  // aucun container ne les référence : sans ce clonage, elles arrivaient
  // orphelines — leur contenu disparaissait, et le calcul de largeur plus bas
  // cassait sur un container introuvable.
  const rowsIdDict = {};
  (sectionContent.rowContainers ?? []).forEach((r) => {
    rowsIdDict[r.id] = uuid();
  });
  const rowContainersToAdd = (sectionContent.rowContainers ?? []).map((r) => ({
    ...r,
    id: rowsIdDict[r.id],
    parentColumnId: columnsIdDict[r.parentColumnId],
    columnsIds: r.columnsIds.map((cId) => columnsIdDict[cId]),
    from_linked_section,
  }));
  // Colonnes orphelines du contenu de la section (référencées par aucun
  // container ni ligne) : les recopier les rendait orphelines dans le document
  // à chaque injection.
  const referencedColumnIds = new Set(
    [...sectionContent.containers, ...(sectionContent.rowContainers ?? [])]
      .flatMap((c) => c?.columnsIds ?? [])
  );
  const columnsToAdd = sectionContent.columns
    .filter((c) => referencedColumnIds.has(c.id))
    .map((c) => {
      return {
        ...c,
        id: columnsIdDict[c.id],
        ...(c.rowContainerIds && {
          rowContainerIds: c.rowContainerIds.map((rId) => rowsIdDict[rId]),
        }),
        from_linked_section,
        linkedMaskHasNotPageNumber:
          c.maskId &&
          sectionContent.masks.find((m) => m.id === c.maskId)?.hasNotPageNumber,
      };
    });
  if (from_linked_section) {
    linkSectionContainer({
      draft,
      sectionId,
      box,
    });
  }
  const header = draft.masks.find(({ id }) => id === MASKS_TYPE.HEADER.id);
  const footer = draft.masks.find(({ id }) => id === MASKS_TYPE.FOOTER.id);
  const containersToAdd = sectionContent.containers.filter((c) => {
    if (!Array.isArray(c?.columnsIds)) {
      console.warn('[mutateInjectSection] container missing columnsIds, skipping', c);
      return false;
    }
    return true;
  }).map((c) => {
    let containerEditability = true;
    if (typeof c?.editable !== 'boolean' || c?.editable)
      containerEditability = true;
    else containerEditability = false;
    let baseContainer = {
      ...c,
      id: uuid(),
      sectionId: fromSectionId,
      from_linked_section,
      // Décor de page de ces pages (cf. mutateBakeSectionPageBackground).
      // Absent = décor du document : on ne le pose pas pour ne pas alourdir le
      // contenu de tous les documents qui n'ont qu'un décor.
      ...(pageBackgroundId && { pageBackgroundId }),
      // Chaîne d'ancêtres (ids de section, racine -> section courante) persistée
      // sur le contenu baké : survit à la suppression de l'ancre (SECTION_LINK
      // dont le container est retiré après injection) et permet de reconstruire
      // l'arbre des sections imbriquées (cf. utils/sectionTree.js).
      sectionPath: Array.isArray(sectionsIds) ? sectionsIds : [sectionId],
      dateTimestamp: dateTimestamp,
      ...(linkedSectionZone && { linkedSection: linkedSectionZone }),
      editable: draft.configuration.editableTemplate && containerEditability,
      columnsIds: c.columnsIds.map((cId) => columnsIdDict[cId]),
      fromBoxId,
      height:
        typeof c.height === 'number' &&
        !sectionContent.columns.find((col) => col.id === c.columnsIds[0])
          ?.maskId &&
        c.height > maxContainerHeight({ header, footer })
          ? maxContainerHeight({ header, footer })
          : c.height,
    };
    if (box.type === ItemTypes.SECTION_VARIABLE) {
      return {
        ...baseContainer,
        fromBoxId,
      };
    }
    return baseContainer;
  });
  // Un style de section identique à un style du document n'est pas importé
  // (cf. mergeStylesForProperty, mutateMergeTmpStyles) : la box se rebranche
  // sur celui du document.
  const styleIdInDocument = (propertyKey, styleId) =>
    resolveSectionStyleId({
      configuration: draft.configuration,
      sectionConfiguration: sectionContent.configuration,
      propertyKey,
      styleId,
      sectionId,
      dateTimestamp,
      importedStyles: linkedInTemplate ? draft.tmp?.[propertyKey] : [],
    });
  const boxesToAdd = mutateDefaultBoxValue(
    sectionContent.boxes.map((b) => {
      let baseBox = {
        ...b,
        oldId: b.id,
        id: `${b.id}-${boxId}-${dateTimestamp}`,
        ...('tableStyleId' in b && {
          tableStyleId: styleIdInDocument('tableStyles', b.tableStyleId),
        }),
        ...('checkboxStyleId' in b && {
          checkboxStyleId: styleIdInDocument('checkboxStyles', b.checkboxStyleId),
        }),
        ...('selectorStyleId' in b && {
          selectorStyleId: styleIdInDocument('selectorStyles', b.selectorStyleId),
        }),
        ...('textStyleId' in b && {
          textStyleId: `${b.textStyleId}_section_${sectionId}_time_${dateTimestamp}`,
        }),
        from_linked_section,
        columnId: columnsIdDict[b.columnId],
        boxSectionId: b.id,
        fromBoxId,
        dateTimestamp,
        fromSectionId,
        // cf. commentaire sur containersToAdd : chaîne d'ancêtres persistée pour
        // l'arbre des sections. Portée sur les box aussi car une ancre récursive
        // (SECTION_VARIABLE) survit et sert de base de chemin à ses descendants.
        sectionPath: Array.isArray(sectionsIds) ? sectionsIds : [sectionId],
      };
      const column = columnsToAdd.find((c) => c.id === baseBox.columnId);
      if (column && !column.drawMode) {
        const container = containersToAdd.find((c) =>
          c.columnsIds.includes(column.id)
        );
        // Sous-colonne d'une ligne : sa largeur découle de la colonne parente,
        // pas de la page. getColumnAvailableWidth remonte la chaîne — on la lui
        // donne sur les tableaux de la section, déjà remappés et cohérents entre
        // eux, avant leur insertion dans le draft.
        const columnWidth = container
          ? computeColumnWidth({
              landscape: draft.landscape,
              marginLeft: draft.configuration.margins.left * gridUnit,
              marginRight: draft.configuration.margins.right * gridUnit,
              containerColumnsLength: container.columnsIds.length,
              size: column.size,
              columnGap: containerColumnGap(container),
            })
          : getColumnAvailableWidth({
              columnId: column.id,
              columns: columnsToAdd,
              containers: containersToAdd,
              rowContainers: rowContainersToAdd,
              landscape: draft.landscape,
              marginLeft: draft.configuration.margins.left * gridUnit,
              marginRight: draft.configuration.margins.right * gridUnit,
            });
        baseBox = reinitializeBoxWidthAndHeight({
          header,
          footer,
          box: baseBox,
          onResize: true,
          columnWidth,
        });
      }
      mutateFormatSectionBox({ box, type: box.type, draft });
      if (isATitleBox(baseBox.type)) {
        baseBox = indentTitles({
          box: baseBox,
          columns: columnsToAdd,
          margins: draft.configuration.margins,
          indentationTitles: draft.configuration.indentationTitles,
        });
      }
      return baseBox;
    }),
    draft.currentDocument.type === 'template'
  );

  boxesToAdd.forEach((b) => {
    if (b.type === ItemTypes.GROUP_BOXES) {
      const boxIds = boxesToAdd
        .filter((bAdd) => b.boxIds.includes(bAdd.oldId))
        .map(({ id }) => id);
      b.boxIds = boxIds;
    }
  });
  boxesToAdd.forEach((b) => {
    delete b.oldId;
  });

  draft.containers.splice(containerIdx + 1, 0, containersToAdd);
  draft.containers = draft.containers.flat();
  if (rowContainersToAdd.length) {
    // Réassignation et non push : les lookups (store/lookups.js) cachent leur
    // Map par référence de tableau, cf. createRowInColumn.
    draft.rowContainers = [...(draft.rowContainers ?? []), ...rowContainersToAdd];
  }
  mutateAddColumns(draft, columnsToAdd);
  mutateAddBoxes({ draft, newBoxes: boxesToAdd });
  mutateInjectTriggersSection({
    sectionContent,
    draft,
    boxesToAdd,
    linked_section,
    from_linked_section,
    dateTimestamp: dateTimestamp,
    fromSectionId,
  });
  mutateSortVariableFromSection({
    draft,
    sectionContent,
    boxesToAdd,
    containerIdx: containerIdx,
    parentContainerId,
    parentId: fromBoxId,
    isDocumentLoading: draft.isDocumentLoading || wasDocumentLoading,
  });
  boxesToAdd.forEach((box) => {
    mutateUpdateTriggerIds(draft, box);
  });
  if (!skipContainerRemoval && box.type !== ItemTypes.SECTION_VARIABLE && !from_linked_section)
    mutateRemoveContainer(draft, draft.containers[containerIdx].id);
  Object.entries(sectionContent.variables).forEach(([key, value]) => {
    if (!value.notUsed && draft.variables[key]?.notUsed) {
      delete draft.variables[key].notUsed;
      if (value.hideField) {
        draft.variables[key].hideField = true;
        draft.variables[key].defaultValue = {
          hideField: true,
        };
      }
    }
    if (
      !draft.variables[key]?.notUsed &&
      draft.currentDocument.type !== 'template'
    ) {
      if (
        draft.variables[key]?.hideField &&
        !value.notUsed &&
        !value.hideField
      ) {
        delete draft.variables[key].hideField;
      }
      if (
        areItemsChangedByTriggers &&
        !draft.itemsChangedByTriggers.some(({ key: iKey }) => key === iKey)
      ) {
        draft.itemsChangedByTriggers.push({ key });
      }
      mutateExecuteParentTriggers({
        draft,
        key: key,
      });
      mutateSearchAndLaunchTrigger({
        triggerId: key,
        draft,
      });
    }
  });
  // SECTION_LINK original IDs that already have baked content in the source.
  // When a section template was saved with a child section already injected, placing
  // its content would produce both the baked child boxes AND a SECTION_LINK anchor.
  // Adding that anchor to sectionsToActivate would inject the child a second time.
  const bakedSectionLinkOriginalIds = new Set(
    sectionContent.boxes.filter((b) => b.fromBoxId).map((b) => b.fromBoxId)
  );

  boxesToAdd.forEach((b) => {
    if (isAVariableBox(b.type) && draft.currentDocument.type !== 'template') {
      if (
        areItemsChangedByTriggers &&
        !draft.itemsChangedByTriggers.some(({ key: iKey }) => b.id === iKey)
      ) {
        draft.itemsChangedByTriggers.push({ key: b.id });
      }
      mutateExecuteParentTriggers({
        draft,
        key: b.id,
      });
      mutateSearchAndLaunchTrigger({
        triggerId: b.id,
        draft,
      });
    }
    if (b.type === ItemTypes.SECTION_LINK && !bakedSectionLinkOriginalIds.has(b.boxSectionId) && !draft.fromPdf) {
      if (validSectionIds && !validSectionIds.has(b.sectionId)) {
        // Lien imbriqué mort (section retirée du catalogue, is_shown=false) :
        // le contrôle de validité des sections liées n'existe qu'au 1er
        // niveau chez les appelants (applyTemplate/applyProposal), jamais
        // pour un section_link découvert ici, au milieu du contenu injecté.
        // Sans ce garde, la section morte est quand même injectée à chaque
        // niveau imbriqué où elle apparaît.
        mutateRemmoveSectionContainers(draft, b.id, {});
      } else {
        draft.sectionsToActivate.unshift({
          boxId: b.id,
          // Section imbriquée elle-même, pas l'ancre englobante : sinon tout
          // container/box de cette sous-section hérite du sectionId du parent
          // (cf. container.sectionId = fromSectionId plus haut), et le panel
          // droit affiche la source de la section parente au lieu de la sienne.
          fromSectionId: b.sectionId,
          sectionId: b.sectionId,
          action: 'inject',
          linked_section: true,
          dateTimestamp,
          isFirstInjection: false,
          fromBoxId,
          isSecondInjection: fromBoxId === boxId,
          from_linked_section,
          sectionsIds: [...sectionsIds, b.sectionId],
          wasDocumentLoading: draft.isDocumentLoading,
          opportunityIdOverride,
          documentTypeOverride,
          parentModel,
          parentId,
          visitedPath,
          validSectionIds,
        });
      }
    }
  });
};

const linkSectionContainer = ({ draft, sectionId, box }) => {
  if (box.type === ItemTypes.SECTION_LINK) return;
  const sectionTitle = resolveSectionTitle(draft, sectionId);
  if (box) {
    box.sectionId = sectionId;
    box.type = ItemTypes.SECTION_LINK;
    box.sectionTitle = sectionTitle;
  }
};

export const mutateDeleteSection = ({ draft, boxId, optionId }) => {
  const sectionsBoxesToDelete = draft.boxes.filter(
    (b) =>
      (b.fromBoxId === boxId || b.sectionVariableBoxId === boxId) &&
      b.type === ItemTypes.SECTION_VARIABLE
  );
  sectionsBoxesToDelete.forEach((sB) => {
    sB.answer = optionId;
    mutateDeleteSection({ draft, boxId: sB.id, optionId });
  });
  const containerToDelete = draft.containers.find(
    (c) => c.fromBoxId === boxId || c.sectionVariableBoxId === boxId
  );

  mutateRemmoveSectionContainers(draft, boxId, {
    sectionId: containerToDelete?.sectionId,
    dateTimestamp: containerToDelete?.dateTimestamp,
  });
  draft.variableOrderArray
    .filter(({ parentId }) => parentId === boxId)
    .forEach(({ id, parentId }) => {
      if (
        draft.variableOrderArray.some(
          ({ id: vId, parentId: vParentId }) =>
            id === vId && parentId !== vParentId
        )
      )
        mutateTakeOutItem(draft, id, parentId);
    });
};

export const getSelectedSection = (sectionId, sectionList) => {
  return (sectionList || []).find((s) => s.id === sectionId);
};

export const shouldHideSectionVariable = ({
  type,
  sectionId,
  sectionList,
  isActivatedByTrigger,
}) =>
  type === ItemTypes.SECTION_VARIABLE &&
  (!getSelectedSection(sectionId, sectionList) || isActivatedByTrigger);

export const mutateHandleLinkedSectionRecursive = ({
  sectionsIds,
  box,
  draft,
  fromBoxId,
  boxId,
  sectionContent,
  isSecondInjection,
}) => {
  if (
    sectionsIds.filter((id) => id === box.sectionId).length > 1 ||
    box.sectionId ===
      (draft.currentDocument.type === 'template'
        ? draft.currentDocument.id
        : draft.currentDocument.id)
  ) {
    mutateRemmoveSectionContainers(draft, fromBoxId);
    draft.triggers = { ...draft.oldTriggers };
    draft.variableOrderArray = [...draft.oldVariableOrderArray];
    draft.oldTriggers = undefined;
    draft.oldVariableOrderArray = undefined;
    return true;
  }
  const parentBoxIds = boxId.split('-');
  sectionContent.boxes.forEach((b) => {
    if (!isAVariableBox(b.type)) return;
    let boxParentId = '';
    for (let idx = 0; idx < parentBoxIds.length; idx += 1) {
      const parentId = parentBoxIds[idx];
      boxParentId = idx === 0 ? parentId : `${boxParentId}-${parentId}`;
      const idToUse = `${b.id}-${boxParentId}`;
      const linkedSectionTriggers = draft.triggers[idToUse]?.filter(
        ({ from_linked_section }) => from_linked_section || isSecondInjection
      );
      if (linkedSectionTriggers?.length > 0) {
        if (
          !sectionContent.triggers[b.id] ||
          sectionContent.triggers[b.id]?.length === 0
        )
          sectionContent.triggers[b.id] = [...linkedSectionTriggers];
        draft.triggers[idToUse] = draft.triggers[idToUse].filter(
          ({ from_linked_section }) =>
            !from_linked_section && !isSecondInjection
        );
        if (draft.triggers[idToUse].length === 0)
          delete draft.triggers[idToUse];
      }

      if (
        draft.variableOrderArray?.find(
          ({ id, parentId: vParentId }) =>
            id === idToUse && fromBoxId === vParentId
        ) &&
        sectionContent.variableOrderArray?.find(({ id }) => id === b.id)
      ) {
        draft.variableOrderArray = draft.variableOrderArray?.filter(
          ({ id, parentId: vParentId }) =>
            id !== idToUse || fromBoxId !== vParentId
        );
      }
    }
  });
  Object.keys(sectionContent.variables).forEach((key) => {
    const linkedSectionTriggers = draft.triggers[key]?.filter(
      ({ from_linked_section }) => from_linked_section || isSecondInjection
    );
    if (linkedSectionTriggers?.length > 0) {
      if (
        !sectionContent.triggers[key] ||
        sectionContent.triggers[key]?.length === 0
      )
        sectionContent.triggers[key] = [...linkedSectionTriggers];
      draft.triggers[key] = draft.triggers[key].filter(
        ({ from_linked_section }) => !from_linked_section && !isSecondInjection
      );
      if (draft.triggers[key].length === 0) delete draft.triggers[key];
    }
    if (
      draft.variableOrderArray?.find(
        ({ id, parentId }) => id === key && parentId === fromBoxId
      ) &&
      sectionContent.variableOrderArray?.find(({ id }) => id === key)
    ) {
      draft.variableOrderArray = draft.variableOrderArray?.filter(
        ({ id, parentId }) => id !== key || parentId !== fromBoxId
      );
    }
  });
  return false;
};
